// biome-ignore-all lint/suspicious/noArrayIndexKey: TODO - address later
// biome-ignore-all lint/suspicious/noImplicitAnyLet: TODO - address later
import { Search } from "lucide-react";
import React from "react";
import { CATEGORY_STYLES } from "@/components/metrics/styles";
import type { MetricCategory } from "@/components/metrics/types";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { ChannelMetrics } from "@/hooks/api/use-channel-metrics";
import {
  type AccountData,
  formatAccountSourceRecordIdsForCSV,
  formatAccountsForCSV,
} from "@/lib/account-data";
import { formatDate } from "@/lib/date-formatting";
import {
  CORE_METRIC_CONFIGS,
  renderAccountLink as coreRenderAccountLink,
  renderOpportunityLink as coreRenderOpportunityLink,
} from "@/lib/metrics/core-metric-configs";
import { formatCurrency } from "@/lib/metrics/format-currency";
import { formatNumber } from "@/lib/metrics/format-number";
import { formatPercentage } from "@/lib/metrics/format-percentage";
import { formatOpportunitiesForCSV } from "@/lib/opportunity-display-utils";
import { cn } from "@/lib/utils";
import type { UnifiedTableColumn } from "@/types/table-column";
import { formatRelationSource } from "@/utils/formatters";

// Use predefined pill colors from CATEGORY_STYLES (still needed for some channel-specific renderers)
const OPPORTUNITY_PILL_CLASSES = CATEGORY_STYLES.Opportunity.pillColor;

// Common types for drilldown items
interface EngagementPeriodItem {
  first_engagement_date?: string;
  last_engagement_date?: string;
}

export const DRILLDOWN_LIMIT = 5000;

// Define WIP metrics

export const WIP_METRICS: Array<keyof ChannelMetrics> = [];

// Define hidden metrics that should be fetched but not displayed
export const HIDDEN_METRICS: Array<keyof ChannelMetrics> = [
  "opportunities_influenced_amount",
  "opportunities_influenced_in_cycle_amount",
  "opportunities_created_post_engagement_amount",
  "progressed_opportunities_amount",
  "opportunities_influenced_won_amount",
  "sourced_opportunities_amount",
  "opportunities_activated_amount",
  "opportunities_activated_won_amount",
  "engagements_per_account",
  "engagements_per_deal_contact",
  "engagements_per_person",
  "days_since_first_engagement",
  "days_since_latest_engagement",
  "first_engagement_date",
  "last_engagement_date",
];

// Define default metric order - same as campaign metrics but without campaign_active_days
export const DEFAULT_METRIC_ORDER: Array<keyof ChannelMetrics> = [
  "accounts_engaged",
  "engagements_per_account",
  "total_engagements",
  "people_engaged",
  "engagements_per_person",
  "deal_contacts_engaged",
  "engagements_per_deal_contact",
  "opportunities_influenced",
  "opportunities_influenced_amount",
  "opportunities_influenced_won",
  "opportunities_influenced_won_amount",
  "sourced_opportunities",
  "sourced_opportunities_amount",
  "opportunities_created_post_engagement",
  "opportunities_created_post_engagement_amount",
  "opportunities_influenced_in_cycle",
  "opportunities_influenced_in_cycle_amount",
  "progressed_opportunities",
  "progressed_opportunities_amount",
  "opportunities_activated",
  "opportunities_activated_amount",
  "opportunities_activated_won",
  "opportunities_activated_won_amount",
  "accounts_activated",
  "accounts_reactivated",
  "accounts_without_opps",
];

// Options for the rendering function
export interface RenderMetricOptions {
  metricKey: keyof ChannelMetrics;
  metrics?: ChannelMetrics;
  benchmarkMetrics?: ChannelMetrics;
  isBenchmark?: boolean;
  isCurrency?: boolean;
  amountMetricKey?: keyof ChannelMetrics;
  secondaryMetricKey?: keyof ChannelMetrics;
  secondaryValue?: number;
  secondaryValueLabel?: React.ReactNode;
}

// Use the shared account link renderer from core configs
const renderAccountLink = coreRenderAccountLink;

// Helper function to create person link rendering
// Used in drilldown configurations
export const renderPersonLink = (item: unknown): React.ReactNode => {
  const typedItem = item as {
    first_name?: string;
    last_name?: string;
    email_address?: string;
    person_id?: string;
    account_id?: string;
  };

  const fullName = [typedItem.first_name, typedItem.last_name]
    .filter(Boolean)
    .join(" ");

  const displayName = fullName || typedItem.email_address || "Unknown";

  if (typedItem.account_id && typedItem.person_id) {
    const explorerUrl = `/accounts/${typedItem.account_id}/explorer?personId=${typedItem.person_id}`;

    return (
      <div>
        <Tooltip>
          <TooltipTrigger asChild>
            <a
              href={explorerUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center hover:text-muted-foreground hover:bg-muted rounded px-2 py-1 -mx-2 -my-1 transition-colors group/name"
            >
              <span>{displayName}</span>
              <Search className="ml-2 h-4 w-4 text-muted-foreground transition-colors opacity-0 group-hover/name:opacity-100" />
            </a>
          </TooltipTrigger>
          <TooltipContent>
            <p>View this person in Account Explorer</p>
          </TooltipContent>
        </Tooltip>
      </div>
    );
  }

  return <div className="font-medium">{displayName}</div>;
};

// Use the shared opportunity link renderer from core configs
const renderOpportunityLink = coreRenderOpportunityLink;

// Helper function to create campaign link rendering
const renderCampaignLink = (item: unknown): React.ReactNode => {
  const typedItem = item as {
    campaign_name?: string;
    campaign_id?: string;
  };
  if (!typedItem.campaign_name || !typedItem.campaign_id) {
    return <span className="italic text-muted-foreground">Unknown</span>;
  }

  // Create link to campaign explorer
  const campaignUrl = `/campaigns/${typedItem.campaign_id}/explorer`;

  return (
    <div>
      <Tooltip>
        <TooltipTrigger asChild>
          <a
            href={campaignUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center hover:text-muted-foreground hover:bg-muted rounded px-2 py-1 -mx-2 -my-1 transition-colors group/name"
          >
            <span>{typedItem.campaign_name}</span>
            <Search className="ml-2 h-4 w-4 text-muted-foreground transition-colors opacity-0 group-hover/name:opacity-100" />
          </a>
        </TooltipTrigger>
        <TooltipContent>
          <p>View this campaign in Campaign Explorer</p>
        </TooltipContent>
      </Tooltip>
    </div>
  );
};

// Unified function for rendering metrics with various display options
export function renderMetric(
  value: number | string | null | undefined,
  options: RenderMetricOptions,
): React.ReactNode {
  // Handle null/undefined values
  if (value === undefined || value === null) return "—";

  // Convert string values to numbers if needed
  const numericValue = typeof value === "string" ? parseFloat(value) : value;

  // --- Primary Value Calculation ---
  const safeValue = numericValue;

  // Determine if we're formatting currency or standard number
  const isCurrency = options.isCurrency || options.metricKey.includes("amount");

  // Format the primary value appropriately
  let formattedValue;
  if (isCurrency) {
    formattedValue = formatCurrency(safeValue);
  } else if (
    options.metricKey.includes("percentage") ||
    options.metricKey.includes("rate")
  ) {
    formattedValue = formatPercentage(safeValue);
  } else if (options.metricKey.includes("date")) {
    formattedValue = safeValue ? formatDate(String(safeValue)) : "—";
  } else {
    formattedValue = formatNumber(safeValue);
  }

  // --- Secondary Content Calculation ---
  let secondaryContentJsx: React.ReactNode = null;

  // Check for secondary metrics
  if (options) {
    // Determine which secondary metric to use (amount or other secondary metric)
    const secondaryKey = options.amountMetricKey || options.secondaryMetricKey;
    const secondaryValue =
      secondaryKey && options.metrics
        ? options.metrics[secondaryKey]
        : options.secondaryValue;

    // Format the secondary value if it exists
    const formattedSecondaryValue =
      typeof secondaryValue === "number"
        ? options.amountMetricKey
          ? formatCurrency(secondaryValue)
          : formatNumber(secondaryValue)
        : null;

    // CASE 1: Custom Label (passed via secondaryValueLabel without secondaryValue/Key)
    if (
      options.secondaryValueLabel &&
      !secondaryKey &&
      options.secondaryValue === undefined
    ) {
      secondaryContentJsx = (
        <span
          className="font-normal whitespace-nowrap"
          title={
            typeof options.secondaryValueLabel === "string"
              ? options.secondaryValueLabel
              : undefined
          }
        >
          {options.secondaryValueLabel}
        </span>
      );
    }
    // CASE 2: Numeric Secondary Value (Amount or Ratio) - Only show if primary value > 0
    else if (
      (secondaryKey || typeof options.secondaryValue === "number") &&
      numericValue > 0
    ) {
      // Create tooltip text for the full secondary metric
      const tooltipText =
        formattedSecondaryValue &&
        options.secondaryValueLabel &&
        typeof options.secondaryValueLabel === "string"
          ? `${formattedSecondaryValue}${options.secondaryValueLabel}`
          : undefined;

      secondaryContentJsx = (
        <span className="inline-flex items-center gap-1" title={tooltipText}>
          {/* Display secondary value */}
          <span>{formattedSecondaryValue}</span>

          {/* Display label if provided */}
          {options.secondaryValueLabel &&
            typeof options.secondaryValueLabel === "string" && (
              <span className="font-normal whitespace-nowrap">
                {options.secondaryValueLabel}
              </span>
            )}
        </span>
      );
    }
  }

  return (
    <div className="flex flex-col">
      {/* Primary Value */}
      <div className="relative flex items-start">
        <span className="text-2xl sm:text-3xl md:text-2xl lg:text-3xl font-bold leading-none">
          {formattedValue}
        </span>
      </div>

      {/* Secondary Content (if any) */}
      <div className="min-h-[20px] sm:min-h-[24px] md:min-h-[20px] lg:min-h-[24px] text-sm sm:text-base md:text-sm lg:text-base text-muted-foreground/50">
        {secondaryContentJsx}
      </div>
    </div>
  );
}

// Define metric data types
export type MetricDataType = "integer" | "float" | "currency" | "date";

// Mapping of each metric to its data type
export const METRIC_DATA_TYPES: Record<keyof ChannelMetrics, MetricDataType> = {
  accounts_engaged: "integer",
  total_engagements: "integer",
  people_engaged: "integer",
  engagements_per_account: "float",
  opportunities_influenced: "integer",
  opportunities_influenced_amount: "currency",
  opportunities_influenced_in_cycle: "integer",
  opportunities_influenced_in_cycle_amount: "currency",
  opportunities_created_post_engagement: "integer",
  opportunities_created_post_engagement_amount: "currency",
  progressed_opportunities: "integer",
  progressed_opportunities_amount: "currency",
  deal_contacts_engaged: "integer",
  engagements_per_deal_contact: "float",
  accounts_reactivated: "integer",
  accounts_activated: "integer",
  engagements_per_person: "float",
  opportunities_influenced_won: "integer",
  opportunities_influenced_won_amount: "currency",
  sourced_opportunities_amount: "currency",
  sourced_opportunities: "integer",
  days_since_first_engagement: "integer",
  days_since_latest_engagement: "integer",
  first_engagement_date: "date",
  last_engagement_date: "date",
  accounts_without_opps: "integer",
  opportunities_activated: "integer",
  opportunities_activated_amount: "currency",
  opportunities_activated_won: "integer",
  opportunities_activated_won_amount: "currency",
};

// Define metric type including drilldown configuration
interface MetricConfig {
  title: string;
  tooltip?: string;
  category: MetricCategory;
  render?: (
    value: number | string | null | undefined,
    options: RenderMetricOptions,
  ) => React.ReactNode;
  drilldownType: "table" | "chart";
  primaryKey: string[];
  drilldownColumns: UnifiedTableColumn[];
  defaultSort?: { key: string; direction: "asc" | "desc" };
}

/**
 * Configuration for each metric
 *
 * NOTE: Several metrics (accounts_engaged, total_engagements, people_engaged,
 * opportunities_influenced, opportunities_influenced_amount, opportunities_influenced_won,
 * opportunities_influenced_won_amount, engagements_per_account) share base structure with
 * CORE_METRIC_CONFIGS from @/lib/metrics/core-metric-configs.
 *
 * Channels extends these with:
 * - Custom render functions for secondary metrics display
 * - Additional drilldown columns (engagement dates, campaign info, job titles, etc.)
 * - Channel-specific tooltips and categorization
 */
export const METRIC_CONFIG: Record<keyof ChannelMetrics, MetricConfig> = {
  // Account metrics
  accounts_engaged: {
    // Import base config from core
    ...CORE_METRIC_CONFIGS.accounts_engaged,
    // Override with Channels-specific title and tooltip
    title: "Accounts Engaged",
    tooltip: "Count of accounts where any person engaged with this channel.",
    // Add Channels-specific render function with secondary metrics
    render: (value, options) => {
      const config = CORE_METRIC_CONFIGS.accounts_engaged;
      const metricsRecord = options.metrics as
        | Record<string, unknown>
        | undefined;
      const secondaryValue =
        config.secondaryMetricKey && metricsRecord
          ? (metricsRecord[config.secondaryMetricKey] as number | undefined)
          : undefined;
      return renderMetric(value, {
        ...options,
        secondaryMetricKey: config.secondaryMetricKey as
          | keyof ChannelMetrics
          | undefined,
        secondaryValueLabel: config.getSecondaryValueLabel?.(secondaryValue),
      });
    },
    // Override with Channels-specific drilldown columns (includes engagement dates)
    drilldownColumns: [
      {
        key: "name",
        title: "Account Name",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
      {
        key: "total_engagements",
        title: "Total Engagements",
        sortable: true,
        filterable: false,
      },
      {
        key: "first_engagement_date",
        dataType: "date",
        title: "First Engagement",
        sortable: true,
        filterable: false,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return typedItem.first_engagement_date
            ? formatDate(typedItem.first_engagement_date)
            : "";
        },
      },
      {
        key: "last_engagement_date",
        dataType: "date",
        title: "Last Engagement",
        sortable: true,
        filterable: false,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return typedItem.last_engagement_date
            ? formatDate(typedItem.last_engagement_date)
            : "";
        },
      },
      {
        key: "account_source_record_id",
        title: "Account Source ID",
        exportOnly: true,
      },
    ],
  },
  accounts_without_opps: {
    title: "Accounts Engaged Outside of Opportunity Cycle",
    tooltip:
      "Count of accounts that have engaged with this channel and have no associated opportunities in the last 90 days (based on opportunity created or close date). This can help identify potential pipeline gaps.",
    category: "Account",
    drilldownType: "table",
    primaryKey: ["account_id"],
    render: (value, options) => {
      return renderMetric(value, {
        ...options,
      });
    },
    drilldownColumns: [
      {
        key: "name",
        title: "Account Name",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          return renderAccountLink(item);
        },
      },
      {
        key: "total_engagements",
        title: "Total Engagements",
        sortable: true,
        filterable: false,
      },
      {
        key: "people_engaged",
        title: "People",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as {
            unique_people?: number;
          };
          return typedItem.unique_people !== undefined
            ? typedItem.unique_people
            : "-";
        },
      },
      {
        key: "first_engagement_date",
        dataType: "date",
        title: "First Engagement",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as { first_engagement_date?: string };
          return typedItem.first_engagement_date
            ? formatDate(typedItem.first_engagement_date)
            : "-";
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as { first_engagement_date?: string };
          return typedItem.first_engagement_date
            ? formatDate(typedItem.first_engagement_date)
            : "";
        },
      },
      {
        key: "last_engagement_date",
        dataType: "date",
        title: "Last Engagement",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as { last_engagement_date?: string };
          return typedItem.last_engagement_date
            ? formatDate(typedItem.last_engagement_date)
            : "-";
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as { last_engagement_date?: string };
          return typedItem.last_engagement_date
            ? formatDate(typedItem.last_engagement_date)
            : "";
        },
      },
      {
        key: "account_source_record_id",
        title: "Account Source ID",
        exportOnly: true,
      },
    ],
    defaultSort: { key: "total_engagements", direction: "desc" },
  },
  accounts_reactivated: {
    title: "Accounts Reactivated",
    tooltip:
      "Count of accounts that had no engagement for 90 days prior to engagement with this channel.",
    category: "Account",
    drilldownType: "table",
    primaryKey: ["account_id"],
    render: (value, options) => {
      return renderMetric(value, {
        ...options,
      });
    },
    drilldownColumns: [
      {
        key: "name",
        title: "Account Name",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          return renderAccountLink(item);
        },
      },
      {
        key: "total_engagements",
        title: "Engagements",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as { total_engagements?: number };
          return typedItem.total_engagements !== undefined
            ? typedItem.total_engagements
            : "-";
        },
      },
      {
        key: "unique_people",
        title: "People",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as {
            unique_people?: number;
          };
          return typedItem.unique_people !== undefined
            ? typedItem.unique_people
            : "-";
        },
      },
      {
        key: "first_engagement_date",
        dataType: "date",
        title: "First Engagement",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as { first_engagement_date?: string };
          return typedItem.first_engagement_date
            ? formatDate(typedItem.first_engagement_date)
            : "-";
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as { first_engagement_date?: string };
          return typedItem.first_engagement_date
            ? formatDate(typedItem.first_engagement_date)
            : "";
        },
      },
      {
        key: "last_engagement_date",
        dataType: "date",
        title: "Last Engagement",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as { last_engagement_date?: string };
          return typedItem.last_engagement_date
            ? formatDate(typedItem.last_engagement_date)
            : "-";
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as { last_engagement_date?: string };
          return typedItem.last_engagement_date
            ? formatDate(typedItem.last_engagement_date)
            : "";
        },
      },
      {
        key: "days_inactive",
        title: "Days Inactive",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as { days_inactive?: number };
          return typedItem.days_inactive !== undefined
            ? typedItem.days_inactive
            : "-";
        },
      },
      {
        key: "account_source_record_id",
        title: "Account Source ID",
        exportOnly: true,
      },
    ],
    defaultSort: { key: "days_inactive", direction: "desc" },
  },
  accounts_activated: {
    title: "Accounts Activated",
    tooltip:
      "Count of accounts that had no engagement at any time prior to engagement with this channel.",
    category: "Account",
    drilldownType: "table",
    primaryKey: ["account_id"],
    render: (value, options) => {
      return renderMetric(value, {
        ...options,
      });
    },
    drilldownColumns: [
      {
        key: "name",
        title: "Account Name",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          return renderAccountLink(item);
        },
      },
      {
        key: "total_engagements",
        title: "Engagements",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as {
            total_engagements?: number;
          };
          return typedItem.total_engagements !== undefined
            ? typedItem.total_engagements
            : "-";
        },
      },
      {
        key: "unique_people",
        title: "People",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as {
            unique_people?: number;
          };
          return typedItem.unique_people !== undefined
            ? typedItem.unique_people
            : "-";
        },
      },
      {
        key: "first_engagement_date",
        dataType: "date",
        title: "First Engagement",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as { first_engagement_date?: string };
          return typedItem.first_engagement_date
            ? formatDate(typedItem.first_engagement_date)
            : "-";
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as { first_engagement_date?: string };
          return typedItem.first_engagement_date
            ? formatDate(typedItem.first_engagement_date)
            : "";
        },
      },
      {
        key: "last_engagement_date",
        dataType: "date",
        title: "Last Engagement",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as { last_engagement_date?: string };
          return typedItem.last_engagement_date
            ? formatDate(typedItem.last_engagement_date)
            : "-";
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as { last_engagement_date?: string };
          return typedItem.last_engagement_date
            ? formatDate(typedItem.last_engagement_date)
            : "";
        },
      },
      {
        key: "account_source_record_id",
        title: "Account Source ID",
        exportOnly: true,
      },
    ],
    defaultSort: { key: "first_engagement_date", direction: "desc" },
  },

  // Engagement metrics
  total_engagements: {
    // Import base config from core
    ...CORE_METRIC_CONFIGS.total_engagements,
    // Override with Channels-specific title and tooltip
    title: "Total Engagements",
    tooltip:
      "Count of total engagements for this channel (may include more than one per person). Excludes any engagements from your own team.",
    // Override category to "Touchpoint" (core uses "Engagement")
    category: "Touchpoint",
    // Add Channels-specific render function
    render: (value, options) => {
      return renderMetric(value, {
        ...options,
      });
    },
    // Override with Channels-specific drilldown columns (includes campaign, export fields)
    drilldownColumns: [
      {
        key: "engagement_date",
        dataType: "date",
        title: "Date",
        sortable: true,
        filterable: false,
        exportValue: (item: unknown): string => {
          const typedItem = item as { engagement_date?: string };
          return typedItem.engagement_date
            ? formatDate(typedItem.engagement_date)
            : "";
        },
      },
      {
        key: "person",
        title: "Person",
        sortable: true,
        filterable: false,
        sortKey: "first_name",
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as { first_name?: string; last_name?: string };
          return (
            <span>
              {typedItem.first_name && typedItem.last_name
                ? `${typedItem.first_name} ${typedItem.last_name}`
                : "Unknown"}
            </span>
          );
        },
        exportable: false,
      },
      {
        key: "first_name",
        title: "First Name",
        exportOnly: true,
      },
      {
        key: "last_name",
        title: "Last Name",
        exportOnly: true,
      },
      {
        key: "email_address",
        title: "Email",
        sortable: true,
        filterable: false,
      },
      {
        key: "accounts",
        title: "Accounts",
        sortable: false,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as {
            accounts?: Array<{
              id: string;
              name: string;
              source_record_id?: string;
            }>;
          };
          if (!typedItem.accounts || typedItem.accounts.length === 0) {
            return "-";
          }
          return (
            <span>
              {typedItem.accounts.map((account, idx) => (
                <React.Fragment key={account.id}>
                  {idx > 0 && ", "}
                  {renderAccountLink({
                    account_id: account.id,
                    name: account.name,
                  })}
                </React.Fragment>
              ))}
            </span>
          );
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as {
            accounts?: Array<{
              id: string;
              name: string;
              source_record_id?: string;
            }>;
          };
          return formatAccountsForCSV(typedItem.accounts);
        },
      },
      {
        key: "account_source_record_ids",
        title: "Account Source IDs",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as {
            accounts?: Array<{
              id: string;
              name: string;
              source_record_id?: string;
            }>;
          };
          return formatAccountSourceRecordIdsForCSV(typedItem.accounts);
        },
      },
      {
        key: "campaign_name",
        title: "Campaign",
        sortable: true,
        filterable: false,
        render: renderCampaignLink,
      },
      {
        key: "campaign_source_record_id",
        title: "Campaign Source ID",
        exportOnly: true,
      },
    ],
    defaultSort: { key: "engagement_date", direction: "desc" },
  },
  people_engaged: {
    title: "People Engaged",
    tooltip:
      "Count of people who engaged with this channel. Excludes any people from your own team.",
    category: "Person",
    drilldownType: "table",
    primaryKey: ["person_id"],
    render: (value, options) => {
      const config = CORE_METRIC_CONFIGS.people_engaged;
      const metricsRecord = options.metrics as
        | Record<string, unknown>
        | undefined;
      const secondaryValue =
        config.secondaryMetricKey && metricsRecord
          ? (metricsRecord[config.secondaryMetricKey] as number | undefined)
          : undefined;
      return renderMetric(value, {
        ...options,
        secondaryMetricKey: config.secondaryMetricKey as
          | keyof ChannelMetrics
          | undefined,
        secondaryValueLabel: config.getSecondaryValueLabel?.(secondaryValue),
      });
    },
    drilldownColumns: [
      {
        key: "contact",
        title: "Contact",
        sortable: true,
        filterable: false,
        sortKey: "first_name",
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as {
            first_name?: string;
            last_name?: string;
            email_address?: string;
          };
          return (
            <div className="flex flex-col">
              <span className="font-medium">
                {typedItem.first_name && typedItem.last_name
                  ? `${typedItem.first_name} ${typedItem.last_name}`
                  : "Unknown"}
              </span>
              <span className="text-sm text-muted-foreground">
                {typedItem.email_address || "No email"}
              </span>
            </div>
          );
        },
        exportable: false,
      },
      {
        key: "first_name",
        title: "First Name",
        exportOnly: true,
      },
      {
        key: "last_name",
        title: "Last Name",
        exportOnly: true,
      },
      {
        key: "email_address",
        title: "Email Address",
        exportOnly: true,
      },
      {
        key: "job_title",
        title: "Job Title",
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as { job_title?: string };
          if (!typedItem.job_title)
            return (
              <span className="italic text-muted-foreground">No data</span>
            );
          return <span>{typedItem.job_title}</span>;
        },
      },
      {
        key: "accounts",
        title: "Accounts",
        sortable: false,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as {
            accounts?: Array<{
              id: string;
              name: string;
              source_record_id?: string;
            }>;
          };
          if (!typedItem.accounts || typedItem.accounts.length === 0) {
            return "-";
          }
          // Render all accounts as comma-separated links
          return (
            <span>
              {typedItem.accounts.map((account, idx) => (
                <React.Fragment key={account.id}>
                  {idx > 0 && ", "}
                  {renderAccountLink({
                    account_id: account.id,
                    name: account.name,
                  })}
                </React.Fragment>
              ))}
            </span>
          );
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as {
            accounts?: AccountData[];
          };
          return formatAccountsForCSV(typedItem.accounts);
        },
      },
      {
        key: "total_engagements",
        title: "Engagements",
        sortable: true,
        filterable: false,
      },
      {
        key: "engagement_period",
        title: "Engagement Period",
        sortable: true,
        filterable: false,
        sortKey: "last_engagement_date",
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as EngagementPeriodItem;
          return (
            <div className="flex flex-col">
              <span className="text-sm text-muted-foreground">
                First:{" "}
                {typedItem.first_engagement_date
                  ? formatDate(typedItem.first_engagement_date)
                  : "N/A"}
              </span>
              <span className="text-sm text-muted-foreground">
                Last:{" "}
                {typedItem.last_engagement_date
                  ? formatDate(typedItem.last_engagement_date)
                  : "N/A"}
              </span>
            </div>
          );
        },
        exportable: false,
      },
      {
        key: "first_engagement_date",
        dataType: "date",
        title: "First Engagement",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return typedItem.first_engagement_date
            ? formatDate(typedItem.first_engagement_date)
            : "";
        },
      },
      {
        key: "last_engagement_date",
        dataType: "date",
        title: "Last Engagement",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return typedItem.last_engagement_date
            ? formatDate(typedItem.last_engagement_date)
            : "";
        },
      },
      {
        key: "account_source_record_ids",
        title: "Account Source IDs",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as {
            accounts?: Array<{
              id: string;
              name: string;
              source_record_id?: string;
            }>;
          };
          return formatAccountSourceRecordIdsForCSV(typedItem.accounts);
        },
      },
    ],
    defaultSort: { key: "total_engagements", direction: "desc" },
  },
  engagements_per_account: {
    title: "Engagements per Account",
    tooltip:
      "Average number of engagements with this channel per account (total engagements divided by number of accounts engaged).",
    category: "Account",
    drilldownType: "table",
    primaryKey: ["account_id"],
    drilldownColumns: [
      {
        key: "accounts",
        title: "Accounts",
        sortable: false,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as {
            accounts?: Array<{
              id: string;
              name: string;
              source_record_id?: string;
            }>;
          };
          if (!typedItem.accounts || typedItem.accounts.length === 0) {
            return "-";
          }
          // Render all accounts as comma-separated links
          return (
            <span>
              {typedItem.accounts.map((account, idx) => (
                <React.Fragment key={account.id}>
                  {idx > 0 && ", "}
                  {renderAccountLink({
                    account_id: account.id,
                    name: account.name,
                  })}
                </React.Fragment>
              ))}
            </span>
          );
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as {
            accounts?: AccountData[];
          };
          return formatAccountsForCSV(typedItem.accounts);
        },
      },
      {
        key: "account_source_record_ids",
        title: "Account Source IDs",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as {
            accounts?: Array<{
              id: string;
              name: string;
              source_record_id?: string;
            }>;
          };
          return formatAccountSourceRecordIdsForCSV(typedItem.accounts);
        },
      },
      {
        key: "total_engagements",
        title: "Total Engagements",
        sortable: true,
        filterable: false,
      },
      {
        key: "unique_people",
        title: "Unique People",
        sortable: true,
        filterable: false,
      },
      {
        key: "engagements_per_person",
        title: "Engagements per Person",
        sortable: true,
        filterable: false,
      },
      {
        key: "account_source_record_id",
        title: "Account Source ID",
        exportOnly: true,
      },
    ],
    defaultSort: { key: "engagements_per_person", direction: "desc" },
    render: (value, options) => {
      return renderMetric(value, {
        ...options,
        secondaryValueLabel: "engagements/account",
      });
    },
  },
  engagements_per_person: {
    title: "Engagements per Person",
    tooltip:
      "Average number of engagements with this channel per person (total engagements divided by number of people engaged).",
    category: "Person",
    drilldownType: "table",
    primaryKey: ["person_id"],
    drilldownColumns: [
      {
        key: "contact",
        title: "Contact",
        sortable: true,
        filterable: false,
        sortKey: "first_name",
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as {
            first_name?: string;
            last_name?: string;
            email_address?: string;
          };
          return (
            <div className="flex flex-col">
              <span className="font-medium">
                {typedItem.first_name && typedItem.last_name
                  ? `${typedItem.first_name} ${typedItem.last_name}`
                  : "Unknown"}
              </span>
              <span className="text-sm text-muted-foreground">
                {typedItem.email_address || "No email"}
              </span>
            </div>
          );
        },
        exportable: false,
      },
      {
        key: "first_name",
        title: "First Name",
        exportOnly: true,
      },
      {
        key: "last_name",
        title: "Last Name",
        exportOnly: true,
      },
      {
        key: "total_engagements",
        title: "Total Engagements",
        sortable: true,
        filterable: false,
      },
      {
        key: "name",
        title: "Account",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
    ],
    defaultSort: { key: "total_engagements", direction: "desc" },
    render: (value, options) => {
      return renderMetric(value, {
        ...options,
        secondaryValueLabel: "engagements/person",
      });
    },
  },

  // Opportunity metrics
  opportunities_influenced: {
    title: "Opportunities Influenced",
    tooltip:
      "Count and total amount of opportunities influenced by this channel.",
    category: "Opportunity",
    drilldownType: "table",
    primaryKey: ["opportunity_id"],
    drilldownColumns: [
      {
        key: "name",
        title: "Opportunity",
        sortable: true,
        filterable: false,
        render: renderOpportunityLink,
        exportValue: (item: unknown): string => {
          const typedItem = item as {
            name?: string | null;
            opportunity_name?: string | null;
          };
          if (typedItem.opportunity_name) return typedItem.opportunity_name;
          if (typedItem.name) return typedItem.name;
          return "";
        },
      },
      {
        key: "name",
        title: "Account",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
      {
        key: "stage_at_engagement",
        title: "Stage at Engagement",
        sortable: true,
        filterable: false,
      },
      {
        key: "current_stage",
        title: "Current Stage",
        sortable: true,
        filterable: false,
      },
      {
        key: "total_amount",
        title: "Opportunity Amount",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const value = item as { total_amount?: number | null };
          return formatCurrency(value.total_amount);
        },
        exportValue: (item: unknown): string => {
          const value = item as { total_amount?: number | null };
          return formatCurrency(value.total_amount);
        },
      },
      {
        key: "created_date",
        dataType: "date",
        title: "Created Date",
        sortable: true,
        filterable: false,
        exportValue: (item: unknown): string => {
          const typedItem = item as { created_date?: string };
          return typedItem.created_date
            ? formatDate(typedItem.created_date)
            : "";
        },
      },
      {
        key: "close_date",
        dataType: "date",
        title: "Close Date",
        sortable: true,
        filterable: false,
        exportValue: (item: unknown): string => {
          const typedItem = item as { close_date?: string };
          return typedItem.close_date ? formatDate(typedItem.close_date) : "";
        },
      },
      {
        key: "total_engagements",
        title: "Engagements",
        sortable: true,
        filterable: false,
      },
      {
        key: "engagement_period",
        title: "Engagement Period",
        sortable: true,
        filterable: false,
        sortKey: "last_engagement_date",
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as EngagementPeriodItem;
          return (
            <div className="flex flex-col">
              <span className="text-sm text-muted-foreground">
                First:{" "}
                {typedItem.first_engagement_date
                  ? formatDate(typedItem.first_engagement_date)
                  : "N/A"}
              </span>
              <span className="text-sm text-muted-foreground">
                Last:{" "}
                {typedItem.last_engagement_date
                  ? formatDate(typedItem.last_engagement_date)
                  : "N/A"}
              </span>
            </div>
          );
        },
        exportable: false,
      },
      {
        key: "first_engagement_date",
        dataType: "date",
        title: "First Engagement",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return typedItem.first_engagement_date
            ? formatDate(typedItem.first_engagement_date)
            : "";
        },
      },
      {
        key: "last_engagement_date",
        dataType: "date",
        title: "Last Engagement",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return typedItem.last_engagement_date
            ? formatDate(typedItem.last_engagement_date)
            : "";
        },
      },
      {
        key: "unique_people",
        title: "People",
        sortable: true,
        filterable: false,
      },
      {
        key: "account_source_record_id",
        title: "Account Source ID",
        exportOnly: true,
      },
      {
        key: "opportunity_source_record_id",
        title: "Source Opportunity ID",
        exportOnly: true,
      },
    ],
    defaultSort: { key: "created_date", direction: "desc" },
    render: (value, options) => {
      return renderMetric(value, {
        ...options,
        amountMetricKey: "opportunities_influenced_amount",
      });
    },
  },
  opportunities_influenced_amount: {
    title: "Sum of Amounts for All Opportunities Influenced",
    tooltip:
      "Sum of the total amount of all opportunities influenced by this channel.",
    category: "Opportunity",
    drilldownType: "table",
    primaryKey: ["opportunity_id"],
    render: (value, options) => {
      return renderMetric(value, {
        ...options,
        isCurrency: true,
      });
    },
    drilldownColumns: [
      {
        key: "name",
        title: "Opportunity",
        sortable: true,
        filterable: false,
        render: renderOpportunityLink,
      },
      {
        key: "name",
        title: "Account",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
      {
        key: "stage_name",
        title: "Stage",
        sortable: true,
        filterable: false,
      },
      {
        key: "total_amount",
        title: "Opportunity Amount",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const value = item as { total_amount?: number | null };
          return formatCurrency(value.total_amount);
        },
        exportValue: (item: unknown): string => {
          const value = item as { total_amount?: number | null };
          return formatCurrency(value.total_amount);
        },
      },
      {
        key: "created_date",
        dataType: "date",
        title: "Created",
        sortable: true,
        filterable: false,
        exportValue: (item: unknown): string => {
          const typedItem = item as { created_date?: string };
          return typedItem.created_date
            ? formatDate(typedItem.created_date)
            : "";
        },
      },
      {
        key: "close_date",
        dataType: "date",
        title: "Close Date",
        sortable: true,
        filterable: false,
        exportValue: (item: unknown): string => {
          const typedItem = item as { close_date?: string };
          return typedItem.close_date ? formatDate(typedItem.close_date) : "";
        },
      },
    ],
    defaultSort: { key: "created_date", direction: "desc" },
  },
  opportunities_influenced_won: {
    title: "Won Opportunities Influenced",
    tooltip:
      "Count and total amount of won opportunities influenced by this channel. An opportunity is considered influenced if any associated contact engaged with the channel.",
    category: "Opportunity",
    drilldownType: "table",
    primaryKey: ["opportunity_id"],
    drilldownColumns: [
      {
        key: "name",
        title: "Opportunity",
        sortable: true,
        filterable: false,
        render: renderOpportunityLink,
        exportValue: (item: unknown): string => {
          const typedItem = item as {
            name?: string | null;
            opportunity_name?: string | null;
          };
          if (typedItem.opportunity_name) return typedItem.opportunity_name;
          if (typedItem.name) return typedItem.name;
          return "";
        },
      },
      {
        key: "name",
        title: "Account",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
      {
        key: "stage_at_engagement",
        title: "Stage at Engagement",
        sortable: true,
        filterable: false,
      },
      {
        key: "current_stage",
        title: "Current Stage",
        sortable: true,
        filterable: false,
      },
      {
        key: "total_amount",
        title: "Opportunity Amount",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const value = item as { total_amount?: number | null };
          return formatCurrency(value.total_amount);
        },
        exportValue: (item: unknown): string => {
          const value = item as { total_amount?: number | null };
          return formatCurrency(value.total_amount);
        },
      },
      {
        key: "created_date",
        dataType: "date",
        title: "Created Date",
        sortable: true,
        filterable: false,
        exportValue: (item: unknown): string => {
          const typedItem = item as { created_date?: string };
          return typedItem.created_date
            ? formatDate(typedItem.created_date)
            : "";
        },
      },
      {
        key: "won_date",
        dataType: "date",
        title: "Won Date",
        sortable: true,
        filterable: false,
        exportValue: (item: unknown): string => {
          const typedItem = item as { won_date?: string };
          return typedItem.won_date ? formatDate(typedItem.won_date) : "";
        },
      },
      {
        key: "total_engagements",
        title: "Engagements",
        sortable: true,
        filterable: false,
      },
      {
        key: "engagement_period",
        title: "Engagement Period",
        sortable: true,
        filterable: false,
        sortKey: "last_engagement_date",
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as EngagementPeriodItem;
          return (
            <div className="flex flex-col">
              <span className="text-sm text-muted-foreground">
                First:{" "}
                {typedItem.first_engagement_date
                  ? formatDate(typedItem.first_engagement_date)
                  : "N/A"}
              </span>
              <span className="text-sm text-muted-foreground">
                Last:{" "}
                {typedItem.last_engagement_date
                  ? formatDate(typedItem.last_engagement_date)
                  : "N/A"}
              </span>
            </div>
          );
        },
        exportable: false,
      },
      {
        key: "first_engagement_date",
        dataType: "date",
        title: "First Engagement",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return typedItem.first_engagement_date
            ? formatDate(typedItem.first_engagement_date)
            : "";
        },
      },
      {
        key: "last_engagement_date",
        dataType: "date",
        title: "Last Engagement",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return typedItem.last_engagement_date
            ? formatDate(typedItem.last_engagement_date)
            : "";
        },
      },
      {
        key: "unique_people",
        title: "People",
        sortable: true,
        filterable: false,
      },
      {
        key: "account_source_record_id",
        title: "Account Source ID",
        exportOnly: true,
      },
      {
        key: "opportunity_source_record_id",
        title: "Source Opportunity ID",
        exportOnly: true,
      },
    ],
    defaultSort: { key: "won_date", direction: "desc" },
    render: (value, options) => {
      return renderMetric(value, {
        ...options,
        amountMetricKey: "opportunities_influenced_won_amount",
      });
    },
  },
  opportunities_influenced_won_amount: {
    title: "Sum of Amounts for Won Opportunities Influenced",
    tooltip:
      "Sum of the total amount of won opportunities influenced by this channel.",
    category: "Opportunity",
    drilldownType: "table",
    primaryKey: ["opportunity_id"],
    render: (value, options) => {
      return renderMetric(value, {
        ...options,
        isCurrency: true,
      });
    },
    drilldownColumns: [
      {
        key: "name",
        title: "Opportunity",
        sortable: true,
        filterable: false,
        render: renderOpportunityLink,
      },
      {
        key: "name",
        title: "Account",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
      {
        key: "stage_name",
        title: "Stage",
        sortable: true,
        filterable: false,
      },
      {
        key: "total_amount",
        title: "Opportunity Amount",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const value = item as { total_amount?: number | null };
          return formatCurrency(value.total_amount);
        },
        exportValue: (item: unknown): string => {
          const value = item as { total_amount?: number | null };
          return formatCurrency(value.total_amount);
        },
      },
      {
        key: "created_date",
        dataType: "date",
        title: "Created",
        sortable: true,
        filterable: false,
        exportValue: (item: unknown): string => {
          const typedItem = item as { created_date?: string };
          return typedItem.created_date
            ? formatDate(typedItem.created_date)
            : "";
        },
      },
      {
        key: "close_date",
        dataType: "date",
        title: "Close Date",
        sortable: true,
        filterable: false,
        exportValue: (item: unknown): string => {
          const typedItem = item as { close_date?: string };
          return typedItem.close_date ? formatDate(typedItem.close_date) : "";
        },
      },
      {
        key: "won_date",
        dataType: "date",
        title: "Won Date",
        sortable: true,
        filterable: false,
        exportValue: (item: unknown): string => {
          const typedItem = item as { won_date?: string };
          return typedItem.won_date ? formatDate(typedItem.won_date) : "";
        },
      },
    ],
    defaultSort: { key: "total_amount", direction: "desc" },
  },
  opportunities_influenced_in_cycle: {
    title: "Opportunities Influenced In-Cycle",
    tooltip:
      "Count and total amount of opportunities influenced by this channel during their active cycle.",
    category: "Opportunity",
    drilldownType: "table",
    primaryKey: ["opportunity_id"],
    drilldownColumns: [
      {
        key: "opportunity_name",
        title: "Opportunity",
        sortable: true,
        filterable: false,
        render: renderOpportunityLink,
      },
      {
        key: "name",
        title: "Account",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
      {
        key: "stage_at_engagement",
        title: "Stage at Engagement",
        sortable: true,
        filterable: false,
      },
      {
        key: "current_stage",
        title: "Current Stage",
        sortable: true,
        filterable: false,
      },
      {
        key: "total_amount",
        title: "Opportunity Amount",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const value = item as { total_amount?: number | null };
          return formatCurrency(value.total_amount);
        },
        exportValue: (item: unknown): string => {
          const value = item as { total_amount?: number | null };
          return formatCurrency(value.total_amount);
        },
      },
      {
        key: "created_date",
        dataType: "date",
        title: "Created Date",
        sortable: true,
        filterable: false,
        exportValue: (item: unknown): string => {
          const typedItem = item as { created_date?: string };
          return typedItem.created_date
            ? formatDate(typedItem.created_date)
            : "";
        },
      },
      {
        key: "close_date",
        dataType: "date",
        title: "Close Date",
        sortable: true,
        filterable: false,
        exportValue: (item: unknown): string => {
          const typedItem = item as { close_date?: string };
          return typedItem.close_date ? formatDate(typedItem.close_date) : "";
        },
      },
      {
        key: "total_engagements",
        title: "Engagements",
        sortable: true,
        filterable: false,
      },
      {
        key: "engagement_period",
        title: "Engagement Period",
        sortable: true,
        filterable: false,
        sortKey: "last_engagement_date",
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as EngagementPeriodItem;
          return (
            <div className="flex flex-col">
              <span className="text-sm text-muted-foreground">
                First:{" "}
                {typedItem.first_engagement_date
                  ? formatDate(typedItem.first_engagement_date)
                  : "N/A"}
              </span>
              <span className="text-sm text-muted-foreground">
                Last:{" "}
                {typedItem.last_engagement_date
                  ? formatDate(typedItem.last_engagement_date)
                  : "N/A"}
              </span>
            </div>
          );
        },
        exportable: false,
      },
      {
        key: "first_engagement_date",
        dataType: "date",
        title: "First Engagement",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return typedItem.first_engagement_date
            ? formatDate(typedItem.first_engagement_date)
            : "";
        },
      },
      {
        key: "last_engagement_date",
        dataType: "date",
        title: "Last Engagement",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return typedItem.last_engagement_date
            ? formatDate(typedItem.last_engagement_date)
            : "";
        },
      },
      {
        key: "unique_people",
        title: "People",
        sortable: true,
        filterable: false,
      },
      {
        key: "account_source_record_id",
        title: "Account Source ID",
        exportOnly: true,
      },
      {
        key: "opportunity_source_record_id",
        title: "Source Opportunity ID",
        exportOnly: true,
      },
    ],
    defaultSort: { key: "created_date", direction: "desc" },
    render: (value, options) => {
      return renderMetric(value, {
        ...options,
        amountMetricKey: "opportunities_influenced_in_cycle_amount",
      });
    },
  },
  opportunities_influenced_in_cycle_amount: {
    title: "Sum of Amounts for Opportunities Influenced In-Cycle",
    tooltip:
      "Sum of the total amount of opportunities influenced by this channel during their active cycle.",
    category: "Opportunity",
    drilldownType: "table",
    primaryKey: ["opportunity_id"],
    render: (value, options) => {
      return renderMetric(value, {
        ...options,
        isCurrency: true,
      });
    },
    drilldownColumns: [
      {
        key: "opportunity_name",
        title: "Opportunity",
        sortable: true,
        filterable: false,
        render: renderOpportunityLink,
      },
      {
        key: "name",
        title: "Account",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
      {
        key: "stage_name",
        title: "Stage",
        sortable: true,
        filterable: false,
      },
      {
        key: "total_amount",
        title: "Opportunity Amount",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const value = item as { total_amount?: number | null };
          return formatCurrency(value.total_amount);
        },
        exportValue: (item: unknown): string => {
          const value = item as { total_amount?: number | null };
          return formatCurrency(value.total_amount);
        },
      },
      {
        key: "created_date",
        dataType: "date",
        title: "Created",
        sortable: true,
        filterable: false,
        exportValue: (item: unknown): string => {
          const typedItem = item as { created_date?: string };
          return typedItem.created_date
            ? formatDate(typedItem.created_date)
            : "";
        },
      },
      {
        key: "close_date",
        dataType: "date",
        title: "Close Date",
        sortable: true,
        filterable: false,
        exportValue: (item: unknown): string => {
          const typedItem = item as { close_date?: string };
          return typedItem.close_date ? formatDate(typedItem.close_date) : "";
        },
      },
    ],
    defaultSort: { key: "created_date", direction: "desc" },
  },
  opportunities_created_post_engagement: {
    title: "Opportunities Created Post-Engagement",
    tooltip:
      "Count and total amount of opportunities that were created after channel engagement.",
    category: "Opportunity",
    drilldownType: "table",
    primaryKey: ["opportunity_id"],
    drilldownColumns: [
      {
        key: "opportunity_name",
        title: "Opportunity",
        sortable: true,
        filterable: false,
        render: renderOpportunityLink,
      },
      {
        key: "name",
        title: "Account",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
      {
        key: "current_stage",
        title: "Current Stage",
        sortable: true,
        filterable: false,
      },
      {
        key: "total_amount",
        title: "Opportunity Amount",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const value = item as { total_amount?: number | null };
          return formatCurrency(value.total_amount);
        },
        exportValue: (item: unknown): string => {
          const value = item as { total_amount?: number | null };
          return formatCurrency(value.total_amount);
        },
      },
      {
        key: "days_till_creation",
        title: "Days from Engagement",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const value = item as { days_till_creation: number };
          return value.days_till_creation !== null
            ? `${value.days_till_creation} days`
            : "N/A";
        },
      },
      {
        key: "created_date",
        dataType: "date",
        title: "Created Date",
        sortable: true,
        filterable: false,
        exportValue: (item: unknown): string => {
          const typedItem = item as { created_date?: string };
          return typedItem.created_date
            ? formatDate(typedItem.created_date)
            : "";
        },
      },
      {
        key: "close_date",
        dataType: "date",
        title: "Close Date",
        sortable: true,
        filterable: false,
        exportValue: (item: unknown): string => {
          const typedItem = item as { close_date?: string };
          return typedItem.close_date ? formatDate(typedItem.close_date) : "";
        },
      },
      {
        key: "engagement_period",
        title: "Engagement Period",
        sortable: true,
        filterable: false,
        sortKey: "last_engagement_date",
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as EngagementPeriodItem;
          return (
            <div className="flex flex-col">
              <span className="text-sm text-muted-foreground">
                First:{" "}
                {typedItem.first_engagement_date
                  ? formatDate(typedItem.first_engagement_date)
                  : "N/A"}
              </span>
              <span className="text-sm text-muted-foreground">
                Last:{" "}
                {typedItem.last_engagement_date
                  ? formatDate(typedItem.last_engagement_date)
                  : "N/A"}
              </span>
            </div>
          );
        },
        exportable: false,
      },
      {
        key: "total_engagements",
        title: "Engagements",
        sortable: true,
        filterable: false,
      },
      {
        key: "first_engagement_date",
        dataType: "date",
        title: "First Engagement",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return typedItem.first_engagement_date
            ? formatDate(typedItem.first_engagement_date)
            : "";
        },
      },
      {
        key: "last_engagement_date",
        dataType: "date",
        title: "Last Engagement",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return typedItem.last_engagement_date
            ? formatDate(typedItem.last_engagement_date)
            : "";
        },
      },
      {
        key: "unique_people",
        title: "People",
        sortable: true,
        filterable: false,
      },
      {
        key: "account_source_record_id",
        title: "Account Source ID",
        exportOnly: true,
      },
      {
        key: "opportunity_source_record_id",
        title: "Source Opportunity ID",
        exportOnly: true,
      },
    ],
    defaultSort: { key: "total_amount", direction: "desc" },
    render: (value, options) => {
      return renderMetric(value, {
        ...options,
        amountMetricKey: "opportunities_created_post_engagement_amount",
      });
    },
  },
  opportunities_created_post_engagement_amount: {
    title: "Sum of Amounts for Opportunities Created Post-Engagement",
    tooltip:
      "Sum of the total amount of opportunities that were created after channel engagement.",
    category: "Opportunity",
    drilldownType: "table",
    primaryKey: ["opportunity_id"],
    render: (value, options) => {
      return renderMetric(value, {
        ...options,
        isCurrency: true,
      });
    },
    drilldownColumns: [
      {
        key: "opportunity_name",
        title: "Opportunity",
        sortable: true,
        filterable: false,
        render: renderOpportunityLink,
      },
      {
        key: "name",
        title: "Account",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
      {
        key: "current_stage",
        title: "Current Stage",
        sortable: true,
        filterable: false,
      },
      {
        key: "total_amount",
        title: "Opportunity Amount",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const value = item as { total_amount?: number | null };
          return formatCurrency(value.total_amount);
        },
        exportValue: (item: unknown): string => {
          const value = item as { total_amount?: number | null };
          return formatCurrency(value.total_amount);
        },
      },
      {
        key: "created_date",
        dataType: "date",
        title: "Created",
        sortable: true,
        filterable: false,
        exportValue: (item: unknown): string => {
          const typedItem = item as { created_date?: string };
          return typedItem.created_date
            ? formatDate(typedItem.created_date)
            : "";
        },
      },
      {
        key: "close_date",
        dataType: "date",
        title: "Close Date",
        sortable: true,
        filterable: false,
        exportValue: (item: unknown): string => {
          const typedItem = item as { close_date?: string };
          return typedItem.close_date ? formatDate(typedItem.close_date) : "";
        },
      },
      {
        key: "total_engagements",
        title: "Engagements",
        sortable: true,
        filterable: false,
      },
      {
        key: "unique_people",
        title: "People",
        sortable: true,
        filterable: false,
      },
    ],
    defaultSort: { key: "total_amount", direction: "desc" },
  },
  progressed_opportunities: {
    title: "Progressed Opportunities",
    tooltip:
      "Count and total amount of opportunities that progressed after channel engagement.",
    category: "Opportunity",
    drilldownType: "table",
    primaryKey: ["name", "account_name"],
    drilldownColumns: [
      {
        key: "name",
        title: "Opportunity",
        sortable: true,
        filterable: false,
        render: renderOpportunityLink,
      },
      {
        key: "name",
        title: "Account",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
      {
        key: "stage_at_engagement",
        title: "Stage at Engagement",
        sortable: true,
        filterable: false,
      },
      {
        key: "stage_progression",
        title: "Stage Progression",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as {
            stage_progression?: string;
            stage_at_engagement?: string;
          };
          if (!typedItem.stage_progression) return "N/A";

          const stages = typedItem.stage_progression.split(" → ");
          const engagementStageIndex = stages.indexOf(
            typedItem.stage_at_engagement ?? "",
          );

          return (
            <span>
              {stages.map((stage, index) => (
                <React.Fragment key={index}>
                  {index > 0 && " → "}
                  <span
                    className={index > engagementStageIndex ? "font-bold" : ""}
                  >
                    {stage}
                  </span>
                </React.Fragment>
              ))}
            </span>
          );
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as { stage_progression?: string };
          if (!typedItem.stage_progression) return "N/A";
          return typedItem.stage_progression;
        },
      },
      {
        key: "total_amount",
        title: "Opportunity Amount",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const value = item as { total_amount?: number | null };
          return formatCurrency(value.total_amount);
        },
        exportValue: (item: unknown): string => {
          const value = item as { total_amount?: number | null };
          return formatCurrency(value.total_amount);
        },
      },
      {
        key: "progressed_date",
        dataType: "date",
        title: "Progressed Date",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const value = item as { progressed_date: string };
          return value.progressed_date
            ? formatDate(value.progressed_date)
            : "N/A";
        },
        exportValue: (item: unknown): string => {
          const value = item as { progressed_date: string };
          return value.progressed_date ? formatDate(value.progressed_date) : "";
        },
      },
      {
        key: "total_engagements",
        title: "Engagements",
        sortable: true,
        filterable: false,
      },
      {
        key: "engagement_period",
        title: "Engagement Period",
        sortable: true,
        filterable: false,
        sortKey: "first_engagement_date",
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as EngagementPeriodItem;
          return (
            <div className="flex flex-col">
              <span className="text-sm text-muted-foreground">
                First:{" "}
                {typedItem.first_engagement_date
                  ? formatDate(typedItem.first_engagement_date)
                  : "N/A"}
              </span>
              <span className="text-sm text-muted-foreground">
                Last:{" "}
                {typedItem.last_engagement_date
                  ? formatDate(typedItem.last_engagement_date)
                  : "N/A"}
              </span>
            </div>
          );
        },
        exportable: false,
      },
      {
        key: "first_engagement_date",
        dataType: "date",
        title: "First Engagement",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return typedItem.first_engagement_date
            ? formatDate(typedItem.first_engagement_date)
            : "";
        },
      },
      {
        key: "last_engagement_date",
        dataType: "date",
        title: "Last Engagement",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return typedItem.last_engagement_date
            ? formatDate(typedItem.last_engagement_date)
            : "";
        },
      },
      {
        key: "unique_people",
        title: "People",
        sortable: true,
        filterable: false,
      },
      {
        key: "account_source_record_id",
        title: "Account Source ID",
        exportOnly: true,
      },
      {
        key: "opportunity_source_record_id",
        title: "Source Opportunity ID",
        exportOnly: true,
      },
    ],
    defaultSort: { key: "progressed_date", direction: "desc" },
    render: (value, options) => {
      return renderMetric(value, {
        ...options,
        amountMetricKey: "progressed_opportunities_amount",
      });
    },
  },
  progressed_opportunities_amount: {
    title: "Sum of Amounts for Progressed Opportunities",
    tooltip:
      "Sum of the total amount of opportunities that progressed after channel engagement.",
    category: "Opportunity",
    drilldownType: "table",
    primaryKey: ["opportunity_id"],
    render: (value, options) => {
      return renderMetric(value, {
        ...options,
        isCurrency: true,
      });
    },
    drilldownColumns: [
      {
        key: "name",
        title: "Opportunity",
        sortable: true,
        filterable: false,
        render: renderOpportunityLink,
      },
      {
        key: "name",
        title: "Account",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
      {
        key: "stage_at_engagement",
        title: "Stage at Engagement",
        sortable: true,
        filterable: false,
      },
      {
        key: "stage_progression",
        title: "Stage Progression",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as {
            stage_progression?: string;
            stage_at_engagement?: string;
          };
          if (!typedItem.stage_progression) return "N/A";

          const stages = typedItem.stage_progression.split(" → ");
          const engagementStageIndex = stages.indexOf(
            typedItem.stage_at_engagement ?? "",
          );

          return (
            <span>
              {stages.map((stage, index) => (
                <React.Fragment key={index}>
                  {index > 0 && " → "}
                  <span
                    className={index > engagementStageIndex ? "font-bold" : ""}
                  >
                    {stage}
                  </span>
                </React.Fragment>
              ))}
            </span>
          );
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as { stage_progression?: string };
          if (!typedItem.stage_progression) return "N/A";
          return typedItem.stage_progression;
        },
      },
      {
        key: "total_amount",
        title: "Opportunity Amount",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const value = item as { total_amount?: number | null };
          return formatCurrency(value.total_amount);
        },
        exportValue: (item: unknown): string => {
          const value = item as { total_amount?: number | null };
          return formatCurrency(value.total_amount);
        },
      },
      {
        key: "progressed_date",
        dataType: "date",
        title: "Progressed Date",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const value = item as { progressed_date: string };
          return value.progressed_date
            ? formatDate(value.progressed_date)
            : "N/A";
        },
        exportValue: (item: unknown): string => {
          const value = item as { progressed_date: string };
          return value.progressed_date ? formatDate(value.progressed_date) : "";
        },
      },
      {
        key: "total_engagements",
        title: "Total Engagements",
        sortable: true,
        filterable: false,
      },
      {
        key: "engagement_period",
        title: "Engagement Period",
        sortable: true,
        filterable: false,
        sortKey: "first_engagement_date",
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as EngagementPeriodItem;
          return (
            <div className="flex flex-col">
              <span className="text-sm text-muted-foreground">
                First:{" "}
                {typedItem.first_engagement_date
                  ? formatDate(typedItem.first_engagement_date)
                  : "N/A"}
              </span>
              <span className="text-sm text-muted-foreground">
                Last:{" "}
                {typedItem.last_engagement_date
                  ? formatDate(typedItem.last_engagement_date)
                  : "N/A"}
              </span>
            </div>
          );
        },
        exportable: false,
      },
      {
        key: "first_engagement_date",
        dataType: "date",
        title: "First Engagement",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return typedItem.first_engagement_date
            ? formatDate(typedItem.first_engagement_date)
            : "";
        },
      },
      {
        key: "last_engagement_date",
        dataType: "date",
        title: "Last Engagement",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return typedItem.last_engagement_date
            ? formatDate(typedItem.last_engagement_date)
            : "";
        },
      },
      {
        key: "unique_people",
        title: "People",
        sortable: true,
        filterable: false,
      },
    ],
    defaultSort: { key: "total_amount", direction: "desc" },
  },
  sourced_opportunities: {
    title: "Sourced opportunities (SFDC)",
    tooltip:
      "Indicates the number and total value of opportunities in Salesforce attributed to campaigns of this channel type, based on the Primary Campaign Source field. Note: this metric may not be available on all dashboards and may differ from Upside-calculated values.",
    category: "Opportunity",
    drilldownType: "table",
    primaryKey: ["opportunity_id"],
    drilldownColumns: [
      {
        key: "name",
        title: "Opportunity",
        sortable: true,
        filterable: false,
        render: renderOpportunityLink,
        exportValue: (item: unknown): string => {
          const typedItem = item as {
            name?: string | null;
            opportunity_name?: string | null;
          };
          if (typedItem.opportunity_name) return typedItem.opportunity_name;
          if (typedItem.name) return typedItem.name;
          return "";
        },
      },
      {
        key: "name",
        title: "Account",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
      {
        key: "current_stage",
        title: "Current Stage",
        sortable: true,
        filterable: false,
      },
      {
        key: "total_amount",
        title: "Opportunity Amount",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const value = item as { total_amount?: number | null };
          return formatCurrency(value.total_amount);
        },
        exportValue: (item: unknown): string => {
          const value = item as { total_amount?: number | null };
          return formatCurrency(value.total_amount);
        },
      },
      {
        key: "created_date",
        dataType: "date",
        title: "Created Date",
        sortable: true,
        filterable: false,
        exportValue: (item: unknown): string => {
          const typedItem = item as { created_date?: string };
          return typedItem.created_date
            ? formatDate(typedItem.created_date)
            : "";
        },
      },
      {
        key: "close_date",
        dataType: "date",
        title: "Close Date",
        sortable: true,
        filterable: false,
        exportValue: (item: unknown): string => {
          const typedItem = item as { close_date?: string };
          return typedItem.close_date ? formatDate(typedItem.close_date) : "";
        },
      },
      {
        key: "total_engagements",
        title: "Engagements",
        sortable: true,
        filterable: false,
      },
      {
        key: "engagement_period",
        title: "Engagement Period",
        tooltip:
          "Note, null engagement periods signifies that the campaign_id associated to this opportunity in SFDC actually had no upside connected touchpoints",
        sortable: true,
        filterable: false,
        sortKey: "last_engagement_date",
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as EngagementPeriodItem;
          return (
            <div className="flex flex-col">
              <span className="text-sm text-muted-foreground">
                First:{" "}
                {typedItem.first_engagement_date
                  ? formatDate(typedItem.first_engagement_date)
                  : "N/A"}
              </span>
              <span className="text-sm text-muted-foreground">
                Last:{" "}
                {typedItem.last_engagement_date
                  ? formatDate(typedItem.last_engagement_date)
                  : "N/A"}
              </span>
            </div>
          );
        },
        exportable: false,
      },
      {
        key: "first_engagement_date",
        dataType: "date",
        title: "First Engagement",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return formatDate(typedItem.first_engagement_date);
        },
      },
      {
        key: "last_engagement_date",
        dataType: "date",
        title: "Last Engagement",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return formatDate(typedItem.last_engagement_date);
        },
      },
      {
        key: "unique_people",
        title: "People",
        sortable: true,
        filterable: false,
      },
      {
        key: "account_source_record_id",
        title: "Account Source ID",
        exportOnly: true,
      },
      {
        key: "opportunity_source_record_id",
        title: "Source Opportunity ID",
        exportOnly: true,
      },
    ],
    defaultSort: { key: "created_date", direction: "desc" },
    render: (
      value: number | string | null | undefined,
      options: {
        metricKey: keyof ChannelMetrics;
        metrics?: ChannelMetrics;
        benchmarkMetrics?: ChannelMetrics;
        isBenchmark?: boolean;
      },
    ) => {
      // This case is already handled properly with string value type
      if (typeof value === "string") {
        return value;
      }
      return renderMetric(value, {
        amountMetricKey: "sourced_opportunities_amount",
        metrics: options.metrics,
        isBenchmark: options.isBenchmark,
        metricKey: options.metricKey,
        benchmarkMetrics: options.benchmarkMetrics,
      });
    },
  },
  sourced_opportunities_amount: {
    title: "Sum of Amounts for Sourced Opportunities (SFDC)",
    tooltip:
      "Sum of the total amount of opportunities in Salesforce attributed to campaigns of this channel type, based on the Primary Campaign Source field. Note: this metric may not be available on all dashboards and may differ from Upside-calculated values.",
    category: "Opportunity",
    drilldownType: "table",
    primaryKey: ["opportunity_id"],
    render: (value, options) => {
      return renderMetric(value, {
        ...options,
        isCurrency: true,
      });
    },
    drilldownColumns: [
      {
        key: "name",
        title: "Opportunity",
        sortable: true,
        filterable: false,
        render: renderOpportunityLink,
      },
      {
        key: "name",
        title: "Account",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
      {
        key: "current_stage",
        title: "Current Stage",
        sortable: true,
        filterable: false,
      },
      {
        key: "total_amount",
        title: "Opportunity Amount",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const value = item as { total_amount?: number | null };
          return formatCurrency(value.total_amount);
        },
        exportValue: (item: unknown): string => {
          const value = item as { total_amount?: number | null };
          return formatCurrency(value.total_amount);
        },
      },
      {
        key: "created_date",
        dataType: "date",
        title: "Created Date",
        sortable: true,
        filterable: false,
        exportValue: (item: unknown): string => {
          const typedItem = item as { created_date?: string };
          return typedItem.created_date
            ? formatDate(typedItem.created_date)
            : "";
        },
      },
      {
        key: "close_date",
        dataType: "date",
        title: "Close Date",
        sortable: true,
        filterable: false,
        exportValue: (item: unknown): string => {
          const typedItem = item as { close_date?: string };
          return typedItem.close_date ? formatDate(typedItem.close_date) : "";
        },
      },
    ],
    defaultSort: { key: "total_amount", direction: "desc" },
  },

  // Contact metrics
  deal_contacts_engaged: {
    title: "Deal Contacts Engaged",
    tooltip:
      "Count of people who engaged with this channel and have been in the buying group for any opportunity. Includes buying group associations via both CRM contact roles and Upside's healed buying groups. Excludes any people from your own team.",
    category: "Person",
    drilldownType: "table",
    primaryKey: ["person_id", "last_name", "first_name"],
    drilldownColumns: [
      {
        key: "contact",
        title: "Contact",
        sortable: true,
        filterable: false,
        sortKey: "last_name",
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as {
            first_name?: string;
            last_name?: string;
            email_address?: string;
          };
          return (
            <div className="flex flex-col">
              <span className="font-medium">
                {typedItem.first_name && typedItem.last_name
                  ? `${typedItem.first_name} ${typedItem.last_name}`
                  : "Unknown"}
              </span>
              <span className="text-sm text-muted-foreground">
                {typedItem.email_address || "No email"}
              </span>
            </div>
          );
        },
        exportable: false,
      },
      {
        key: "first_name",
        title: "First Name",
        exportOnly: true,
      },
      {
        key: "last_name",
        title: "Last Name",
        exportOnly: true,
      },
      {
        key: "accounts",
        title: "Accounts",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
      {
        key: "job_title",
        title: "Job Title",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as { job_title?: string };
          if (!typedItem.job_title)
            return (
              <span className="italic text-muted-foreground">No data</span>
            );
          return <span>{typedItem.job_title}</span>;
        },
      },
      {
        key: "relation_source",
        title: "Relation Source",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as { relation_source?: string };
          return (
            <span>
              {typedItem.relation_source
                ? formatRelationSource(typedItem.relation_source)
                : ""}
            </span>
          );
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as { relation_source?: string };
          return typedItem.relation_source
            ? formatRelationSource(typedItem.relation_source)
            : "";
        },
      },
      {
        key: "opportunities",
        title: "Opportunities",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as {
            opportunities?: Array<{
              id: string;
              name: string;
              source_record_id: string;
              account_id: string;
            }>;
          };

          const opportunities = typedItem.opportunities;
          if (!opportunities || opportunities.length === 0) return "-";

          return (
            <div className="flex flex-wrap gap-1">
              {opportunities.map((opportunity) => {
                const { id: oppId, name, account_id: accountId } = opportunity;

                if (oppId && accountId) {
                  return (
                    <Tooltip key={oppId}>
                      <TooltipTrigger asChild>
                        <a
                          href={`/accounts/${accountId}/explorer?opportunityId=${oppId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-pill
                          className={cn(
                            "inline-flex items-center rounded-md px-2 py-1 text-xs font-medium",
                            OPPORTUNITY_PILL_CLASSES,
                          )}
                        >
                          {name}
                        </a>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>View this opportunity in Account Explorer</p>
                      </TooltipContent>
                    </Tooltip>
                  );
                }
                // Fallback to non-clickable badge if no ID
                return (
                  <span
                    key={oppId || name}
                    className={cn(
                      "inline-flex items-center rounded-md px-2 py-1 text-xs font-medium",
                      OPPORTUNITY_PILL_CLASSES.replace(
                        "cursor-pointer",
                        "",
                      ).replace(
                        "hover:bg-supporting-green/20 dark:hover:bg-supporting-green/20 transition-colors",
                        "",
                      ),
                    )}
                  >
                    {name}
                  </span>
                );
              })}
            </div>
          );
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as {
            opportunities?: Array<{ name: string }>;
          };
          return (
            typedItem.opportunities?.map((opp) => opp.name).join(", ") || ""
          );
        },
      },
      {
        key: "total_engagements",
        title: "Engagements",
        sortable: true,
        filterable: false,
      },
      {
        key: "engagement_period",
        title: "Engagement Period",
        sortable: true,
        filterable: false,
        sortKey: "last_engagement_date",
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as EngagementPeriodItem;
          return (
            <div className="flex flex-col">
              <span className="text-sm text-muted-foreground">
                First:{" "}
                {typedItem.first_engagement_date
                  ? formatDate(typedItem.first_engagement_date)
                  : "N/A"}
              </span>
              <span className="text-sm text-muted-foreground">
                Last:{" "}
                {typedItem.last_engagement_date
                  ? formatDate(typedItem.last_engagement_date)
                  : "N/A"}
              </span>
            </div>
          );
        },
        exportable: false,
      },
      {
        key: "first_engagement_date",
        dataType: "date",
        title: "First Engagement",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return typedItem.first_engagement_date
            ? formatDate(typedItem.first_engagement_date)
            : "";
        },
      },
      {
        key: "last_engagement_date",
        dataType: "date",
        title: "Last Engagement",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as EngagementPeriodItem;
          return typedItem.last_engagement_date
            ? formatDate(typedItem.last_engagement_date)
            : "";
        },
      },
      {
        key: "opportunity_source_record_ids",
        title: "Source Opportunity IDs",
        exportOnly: true,
      },
      {
        key: "account_source_record_id",
        title: "Source Account ID",
        exportOnly: true,
      },
    ],
    defaultSort: { key: "total_engagements", direction: "desc" },
    render: (value, options) => {
      const config = CORE_METRIC_CONFIGS.deal_contacts_engaged;
      const metricsRecord = options.metrics as
        | Record<string, unknown>
        | undefined;
      const secondaryValue =
        config.secondaryMetricKey && metricsRecord
          ? (metricsRecord[config.secondaryMetricKey] as number | undefined)
          : undefined;
      return renderMetric(value, {
        ...options,
        secondaryMetricKey: config.secondaryMetricKey as
          | keyof ChannelMetrics
          | undefined,
        secondaryValueLabel: config.getSecondaryValueLabel?.(secondaryValue),
      });
    },
  },
  engagements_per_deal_contact: {
    title: "Engagements per Deal Contact",
    tooltip:
      "Average number of engagements with this channel per buying group person. Includes buying group associations via both CRM contact roles and Upside's healed buying groups. Excludes any people from your own team.",
    category: "Person",
    drilldownType: "table",
    primaryKey: ["person_id", "account_id"],
    drilldownColumns: [
      {
        key: "contact",
        title: "Contact",
        sortable: true,
        filterable: false,
        sortKey: "last_name",
        render: (item: unknown): React.ReactNode => {
          return renderAccountLink(item);
        },
        exportable: false,
      },
      {
        key: "first_name",
        title: "First Name",
        exportOnly: true,
      },
      {
        key: "last_name",
        title: "Last Name",
        exportOnly: true,
      },
      {
        key: "opportunities",
        title: "Opportunities",
        sortable: true,
        filterable: false,
        exportValue: (item: unknown): string => {
          const typedItem = item as {
            opportunities?: unknown;
          };
          return formatOpportunitiesForCSV(typedItem.opportunities);
        },
      },
      {
        key: "total_engagements",
        title: "Total Engagements",
        sortable: true,
        filterable: false,
      },
      {
        key: "engagements_per_opportunity",
        title: "Engagements per Opportunity",
        sortable: true,
        filterable: false,
      },
    ],
    defaultSort: { key: "engagements_per_opportunity", direction: "desc" },
    render: (value, options) => {
      return renderMetric(value, {
        ...options,
        secondaryValueLabel: ` ${
          options.secondaryValue === 1 ? "engagement" : "engagements"
        }/opportunity`,
      });
    },
  },

  // Time-based metrics
  days_since_first_engagement: {
    title: "Days since first engagement",
    tooltip: "Number of days since the first engagement for this channel",
    category: "Channel",
    drilldownType: "table",
    primaryKey: ["id"],
    drilldownColumns: [],
    render: (value, options) => {
      if (value === null || value === undefined) return "—";
      return renderMetric(value, {
        ...options,
        secondaryValueLabel: " days",
      });
    },
  },
  days_since_latest_engagement: {
    title: "Days since latest engagement",
    tooltip: "Number of days since the latest engagement for this channel",
    category: "Channel",
    drilldownType: "table",
    primaryKey: ["id"],
    drilldownColumns: [],
    render: (value, options) => {
      if (value === null || value === undefined) return "—";
      return renderMetric(value, {
        ...options,
        secondaryValueLabel: " days",
      });
    },
  },
  first_engagement_date: {
    title: "First engagement date",
    tooltip: "Date of the first engagement for this channel",
    category: "Channel",
    drilldownType: "table",
    primaryKey: ["id"],
    drilldownColumns: [],
    render: (value, options) => {
      if (typeof value === "string") {
        return renderMetric(value, {
          ...options,
          metricKey: "first_engagement_date",
        });
      }
      return "—";
    },
  },
  last_engagement_date: {
    title: "Last engagement date",
    tooltip: "Date of the latest engagement for this channel",
    category: "Channel",
    drilldownType: "table",
    primaryKey: ["id"],
    drilldownColumns: [],
    render: (value, options) => {
      if (typeof value === "string") {
        return renderMetric(value, {
          ...options,
          metricKey: "last_engagement_date",
        });
      }
      return "—";
    },
  },
  opportunities_activated: {
    title: "Opportunities with Activation Milestone Citation",
    tooltip:
      "Count of opportunities where AI analysis identified this channel's touchpoints as influential in opportunity activation.",
    category: "Opportunity",
    drilldownType: "table",
    primaryKey: ["opportunity_id"],
    drilldownColumns: [
      {
        key: "name",
        title: "Opportunity",
        sortable: true,
        filterable: false,
        render: renderOpportunityLink,
        exportValue: (item: unknown): string => {
          const typedItem = item as {
            name?: string | null;
            opportunity_name?: string | null;
          };
          if (typedItem.opportunity_name) return typedItem.opportunity_name;
          if (typedItem.name) return typedItem.name;
          return "";
        },
      },
      {
        key: "accounts",
        title: "Accounts",
        sortable: false,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as {
            accounts?: Array<{
              id: string;
              name: string;
              source_record_id?: string;
            }>;
          };
          if (!typedItem.accounts || typedItem.accounts.length === 0) {
            return "-";
          }
          // Render all accounts as comma-separated links
          return (
            <span>
              {typedItem.accounts.map((account, idx) => (
                <React.Fragment key={account.id}>
                  {idx > 0 && ", "}
                  {renderAccountLink({
                    account_id: account.id,
                    name: account.name,
                  })}
                </React.Fragment>
              ))}
            </span>
          );
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as {
            accounts?: AccountData[];
          };
          return formatAccountsForCSV(typedItem.accounts);
        },
      },
      {
        key: "account_source_record_ids",
        title: "Account Source IDs",
        exportOnly: true,
        exportValue: (item: unknown): string => {
          const typedItem = item as {
            accounts?: Array<{
              id: string;
              name: string;
              source_record_id?: string;
            }>;
          };
          return formatAccountSourceRecordIdsForCSV(typedItem.accounts);
        },
      },
      {
        key: "current_stage",
        title: "Current Stage",
        sortable: true,
        filterable: false,
      },
      {
        key: "total_amount",
        title: "Opportunity Amount",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const value = item as { total_amount?: number | null };
          return formatCurrency(value.total_amount);
        },
        exportValue: (item: unknown): string => {
          const value = item as { total_amount?: number | null };
          return formatCurrency(value.total_amount);
        },
      },
      {
        key: "is_won",
        title: "Status",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as { is_won?: boolean };
          return typedItem.is_won ? (
            <span className="text-success font-medium">Won</span>
          ) : (
            <span className="text-muted-foreground">Open</span>
          );
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as { is_won?: boolean };
          return typedItem.is_won ? "Won" : "Open";
        },
      },
      {
        key: "analysis_type",
        title: "Analysis Type",
        sortable: true,
        filterable: false,
      },
      {
        key: "influence_source",
        title: "Influence Source",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as { influence_source?: string };
          const source = typedItem.influence_source || "";
          const labelMap: Record<string, string> = {
            touchpoint_citations: "Touchpoint Citations",
            campaign_influences: "Campaign Influences",
            channel_influences: "Channel Influences",
          };
          return labelMap[source] || source;
        },
      },
      {
        key: "total_engagements",
        title: "Channel Touchpoints",
        sortable: true,
        filterable: false,
      },
      {
        key: "created_date",
        dataType: "date",
        title: "Created Date",
        sortable: true,
        filterable: false,
        exportValue: (item: unknown): string => {
          const typedItem = item as { created_date?: string };
          return typedItem.created_date
            ? formatDate(typedItem.created_date)
            : "";
        },
      },
      {
        key: "close_date",
        dataType: "date",
        title: "Close Date",
        sortable: true,
        filterable: false,
        exportValue: (item: unknown): string => {
          const typedItem = item as { close_date?: string };
          return typedItem.close_date ? formatDate(typedItem.close_date) : "";
        },
      },
      {
        key: "account_source_record_id",
        title: "Account Source ID",
        exportOnly: true,
      },
      {
        key: "opportunity_source_record_id",
        title: "Source Opportunity ID",
        exportOnly: true,
      },
    ],
    defaultSort: { key: "total_amount", direction: "desc" },
    render: (value, options) => {
      return renderMetric(value, {
        ...options,
        amountMetricKey: "opportunities_activated_amount",
      });
    },
  },
  opportunities_activated_amount: {
    title: "Sum of Amounts for All Opportunities Activated",
    tooltip:
      "Sum of the total amount of all opportunities where AI analysis identified this channel's touchpoints as influential.",
    category: "Opportunity",
    drilldownType: "table",
    primaryKey: ["opportunity_id"],
    render: (value, options) => {
      return renderMetric(value, {
        ...options,
        isCurrency: true,
      });
    },
    drilldownColumns: [], // Uses same drilldown as opportunities_activated
  },
  opportunities_activated_won: {
    title: "Won Opportunities with Activation Milestone Citation",
    tooltip:
      "Count of won opportunities where AI analysis identified this channel's touchpoints as influential.",
    category: "Opportunity",
    drilldownType: "table",
    primaryKey: ["opportunity_id"],
    drilldownColumns: [
      {
        key: "name",
        title: "Opportunity",
        sortable: true,
        filterable: false,
        render: renderOpportunityLink,
        exportValue: (item: unknown): string => {
          const typedItem = item as {
            name?: string | null;
            opportunity_name?: string | null;
          };
          if (typedItem.opportunity_name) return typedItem.opportunity_name;
          if (typedItem.name) return typedItem.name;
          return "";
        },
      },
      {
        key: "name",
        title: "Account",
        sortable: true,
        filterable: false,
        render: renderAccountLink,
      },
      {
        key: "current_stage",
        title: "Current Stage",
        sortable: true,
        filterable: false,
      },
      {
        key: "total_amount",
        title: "Opportunity Amount",
        sortable: true,
        filterable: false,
        render: (item: unknown) => {
          const value = item as { total_amount?: number | null };
          return formatCurrency(value.total_amount);
        },
        exportValue: (item: unknown): string => {
          const value = item as { total_amount?: number | null };
          return formatCurrency(value.total_amount);
        },
      },
      {
        key: "is_won",
        title: "Status",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as { is_won?: boolean };
          return typedItem.is_won ? (
            <span className="text-success font-medium">Won</span>
          ) : (
            <span className="text-muted-foreground">Open</span>
          );
        },
        exportValue: (item: unknown): string => {
          const typedItem = item as { is_won?: boolean };
          return typedItem.is_won ? "Won" : "Open";
        },
      },
      {
        key: "analysis_type",
        title: "Analysis Type",
        sortable: true,
        filterable: false,
      },
      {
        key: "influence_source",
        title: "Influence Source",
        sortable: true,
        filterable: false,
        render: (item: unknown): React.ReactNode => {
          const typedItem = item as { influence_source?: string };
          const source = typedItem.influence_source || "";
          const labelMap: Record<string, string> = {
            touchpoint_citations: "Touchpoint Citations",
            campaign_influences: "Campaign Influences",
            channel_influences: "Channel Influences",
          };
          return labelMap[source] || source;
        },
      },
      {
        key: "total_engagements",
        title: "Channel Touchpoints",
        sortable: true,
        filterable: false,
      },
      {
        key: "created_date",
        dataType: "date",
        title: "Created Date",
        sortable: true,
        filterable: false,
        exportValue: (item: unknown): string => {
          const typedItem = item as { created_date?: string };
          return typedItem.created_date
            ? formatDate(typedItem.created_date)
            : "";
        },
      },
      {
        key: "close_date",
        dataType: "date",
        title: "Close Date",
        sortable: true,
        filterable: false,
        exportValue: (item: unknown): string => {
          const typedItem = item as { close_date?: string };
          return typedItem.close_date ? formatDate(typedItem.close_date) : "";
        },
      },
      {
        key: "account_source_record_id",
        title: "Account Source ID",
        exportOnly: true,
      },
      {
        key: "opportunity_source_record_id",
        title: "Source Opportunity ID",
        exportOnly: true,
      },
    ],
    defaultSort: { key: "total_amount", direction: "desc" },
    render: (value, options) => {
      return renderMetric(value, {
        ...options,
        amountMetricKey: "opportunities_activated_won_amount",
      });
    },
  },
  opportunities_activated_won_amount: {
    title:
      "Sum of Amounts for All Won Opportunities with Activation Milestone Citation",
    tooltip:
      "Sum of the total amount of all won opportunities where AI analysis identified this channel's touchpoints as influential.",
    category: "Opportunity",
    drilldownType: "table",
    primaryKey: ["opportunity_id"],
    render: (value, options) => {
      return renderMetric(value, {
        ...options,
        isCurrency: true,
      });
    },
    drilldownColumns: [],
  },
};
